import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckBadgeIcon } from '@heroicons/react/24/outline';
import { Button } from '../../catalyst/button';
import { Field, Label } from '../../catalyst/fieldset';
import { Select } from '../../catalyst/select';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { usePlatformData } from '../../hooks/usePlatformData';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useSessionStorageState } from '../../hooks/useSessionStorageState';
import { useCurationSession } from './useCurationSession';
import { membersInSnapshot } from './groupVerdict';
import { CategoryPickerDialog } from './CategoryPickerDialog';
import { CurrentItem } from './CurrentItem';
import { SessionSummaryPanel } from './SessionSummaryPanel';
import { EmptyQueuePanel } from './EmptyQueuePanel';

const SOURCE_OPTIONS = [
  { label: 'All sources', value: '' },
  { label: 'Flags', value: 'manual_flag' },
  { label: 'Completeness', value: 'completeness' },
  { label: 'User suggested', value: 'user_mismatch' },
  { label: 'Second opinion', value: 'second_opinion' },
  { label: 'Outliers', value: 'outlier' },
  { label: 'Low certainty', value: 'low_certainty' },
] as const;

export default function Curation() {
  useDocumentTitle('Curation');

  // Filters persist in sessionStorage so they survive a reload or navigating
  // away and back within the tab session. The table's "Start review" hand-off
  // (?source=&category=) seeds the initial value so it wins over the restored
  // filters from the very first render/fetch (an empty param means "all"); the
  // in-page controls own it after. No params → restore the persisted filters.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSource = searchParams.get('source');
  const urlCategory = searchParams.get('category');
  const urlSeed =
    urlSource !== null || urlCategory !== null
      ? { source: urlSource ?? '', category: urlCategory ?? '' }
      : undefined;
  const [persistedFilters, setPersistedFilters] = useSessionStorageState(
    'curation-focus-filters',
    { source: '', category: '' },
    urlSeed,
  );
  const sourceFilter = persistedFilters.source;
  const categoryFilter = persistedFilters.category;
  const setSourceFilter = (value: string) => setPersistedFilters((f) => ({ ...f, source: value }));
  const setCategoryFilter = (value: string) => setPersistedFilters((f) => ({ ...f, category: value }));

  // The hand-off is one-time: once its params have seeded the filters above (and
  // been persisted), strip them from the URL so a later reload restores the
  // up-to-date session filters rather than re-applying the now-stale link. Only
  // touches the URL — the filter state is already correct from the seed — so the
  // first render/fetch is unaffected.
  useEffect(() => {
    if (searchParams.has('source') || searchParams.has('category')) {
      setSearchParams(
        (prev) => {
          prev.delete('source');
          prev.delete('category');
          return prev;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [pickerOpen, setPickerOpen] = useState(false);
  // The group as it stood when the picker opened. The picker is a second screen, so a
  // cohort that arrives (or recovers from an error) behind it must not join the ruling —
  // the curator only ever saw these pages. null = picker closed / not armed (DR-3318).
  const [pickerSnapshot, setPickerSnapshot] = useState<ReadonlySet<string> | null>(null);

  const filters = { source: sourceFilter || undefined, category: categoryFilter || undefined };
  const {
    loading,
    error,
    refetch,
    total,
    loaded,
    pending,
    current,
    stats,
    members,
    selectedMembers,
    selectedMemberIds,
    toggleMember,
    membersSettled,
    blastRadius,
    confirmRadius,
    confirm,
    correct,
    skip,
    fetchMore,
    saving,
  } = useCurationSession(filters);

  // Still-selected members that were already on screen when the picker opened — the only
  // pages a pick from that dialog may land on, and the count it advertises.
  const pickerMembers = useMemo(
    () => membersInSnapshot(selectedMembers, pickerSnapshot),
    [selectedMembers, pickerSnapshot],
  );

  // Position in the loaded batch: counts everything handled (reviewed + skipped).
  // This is NOT a review count — skips advance it but are never saved as reviews.
  const processedInBatch = loaded - pending.length;

  const headerRight = (
    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-zinc-400">
      {total > 0 && (
        <span
          className="font-medium text-gray-900 dark:text-zinc-100"
          title="Position in batch (reviewed + skipped)"
        >
          {processedInBatch}/{loaded}
          {total > loaded && (
            <span className="ml-1 font-normal text-gray-500 dark:text-zinc-400">· {total} flagged</span>
          )}
        </span>
      )}
      {stats.reviewed > 0 && (
        <span>
          {stats.reviewed} reviewed
          {stats.streak > 0 && <span className="ml-1">🔥{stats.streak}</span>}
        </span>
      )}
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Curation"
        description="Review and correct likely-mislabeled document pages"
        icon={<CheckBadgeIcon className="size-6" />}
      >
        {headerRight}
      </PageHeader>

      {/* Filter row */}
      <div className="flex flex-wrap items-end gap-4">
        <Field className="w-full sm:w-56">
          <Label htmlFor="source">Source</Label>
          <Select id="source" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            {SOURCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Field>
        <CategoryFilter value={categoryFilter} onChange={setCategoryFilter} />
      </div>

      {/* Loading state */}
      {loading && (
        <div className="space-y-4">
          <Skeleton variant="rect" className="h-96 w-full" />
          <Skeleton lines={3} />
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 p-8 text-center space-y-4">
          <p className="text-red-600 dark:text-red-400 text-sm">Failed to load curation queue.</p>
          <Button outline onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}

      {/* Empty / summary state.
          "Queue complete" + retrain only when something was actually reviewed;
          otherwise (empty queue OR every item skipped) show the neutral panel. */}
      {!loading && !error && !current &&
        (stats.reviewed > 0 ? (
          <SessionSummaryPanel stats={stats} onFetchMore={fetchMore} />
        ) : (
          <EmptyQueuePanel total={total} onRefresh={fetchMore} />
        ))}

      {/* Current item */}
      {!loading && !error && current && (
        <CurrentItem
          key={current.pageId}
          item={current}
          saving={saving}
          pickerOpen={pickerOpen}
          members={members}
          selectedMemberIds={selectedMemberIds}
          membersSettled={membersSettled}
          blastRadius={blastRadius}
          confirmRadius={confirmRadius}
          onToggleMember={toggleMember}
          onWrong={() => {
            setPickerSnapshot(new Set(selectedMemberIds));
            setPickerOpen(true);
          }}
          onConfirm={() => confirm(current)}
          onSkip={skip}
        />
      )}

      <CategoryPickerDialog
        open={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          setPickerSnapshot(null);
        }}
        current={current?.currentCategory ?? null}
        suggested={current?.suggestedCategory ?? null}
        blastRadius={1 + pickerMembers.length}
        membersSettled={membersSettled}
        onPick={(name) => {
          if (current) correct(current, name, pickerMembers);
          setPickerOpen(false);
          setPickerSnapshot(null);
        }}
      />
    </div>
  );
}

function CategoryFilter({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { categories } = usePlatformData();
  return (
    <Field className="w-full sm:w-56">
      <Label htmlFor="category">Category</Label>
      <Select
        id="category"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={categories.isLoading}
      >
        <option value="">All categories</option>
        {(categories.data?.pageCategories ?? []).map((category) => (
          <option key={category} value={category}>
            {category}
          </option>
        ))}
      </Select>
    </Field>
  );
}
